import { sendUpdateEmail } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Icons,
  Input,
  Label,
  LoadingIndicator,
  Textarea,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dispatch, SetStateAction } from 'react';
import { useForm } from '../../../hooks/useZodForm';
import {
  ServiceRequestUpdateEmailSchema,
  ServiceRequestUpdateEmailSchemaFields,
} from '../schema/email-schema';

type SendEmailModalProps = {
  config?: {
    heading?: string;
    subHeading?: string | null;
    subject: string | null;
    body: string;
    onSuccess?: () => void;
    buttonText?: string;
  };
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  serviceRequest: ServiceRequestDetails;
};

export const SendEmailModal = ({
  isOpen,
  setIsOpen,
  config,
  serviceRequest,
}: SendEmailModalProps) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ServiceRequestUpdateEmailSchemaFields>({
    resolver: zodResolver(ServiceRequestUpdateEmailSchema),
    defaultValues: {
      body: config?.body,
      subject: config?.subject,
    },
  });

  const onSubmit = async (formData: ServiceRequestUpdateEmailSchemaFields) => {
    const res = await sendUpdateEmail({
      serviceRequestId: serviceRequest.id,
      details: formData.body,
      subject: formData.subject,
    });
    if (!res.ok) {
      console.error('Error while sending email.');
      return;
    }
    if (config?.onSuccess) config.onSuccess();
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-3xl w-full" onOpenAutoFocus={(e) => e.preventDefault()}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <div>
              <h3 className="text-lg text-text-primary-900">
                {config?.heading || 'Send Email Updates'}
              </h3>
              {config?.subHeading ? (
                <span className="text-sm text-text-tertiary-600">
                  {config?.subHeading}
                </span>
              ) : null}
            </div>
          </DialogHeader>
          <div className="px-6 py-5 flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label className="text-text-secondary-700">Subject</Label>
              <Input
                type="text"
                {...register('subject')}
                value={watch('subject')}
                error={errors.subject?.message}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-text-secondary-700">Body</Label>
              <Textarea
                {...register('body')}
                value={watch('body')}
                error={errors.body?.message}
                className="h-[250px]"
              />
            </div>
          </div>

          <DialogFooter>
            <div className="flex justify-end">
              <Button
                type="submit"
                className="flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingIndicator /> : null}
                Send Update <Icons.Send01 className="w-5" />
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
